"use client";
import { useEffect, useRef, useState } from "react";
import type { AnalyticsEvent } from "~/connectors/analytics.connector";
import { trackEvent } from "~/connectors/analytics.connector";


interface AnalyticsViewportTrackerProps {
  children: React.ReactNode;
  eventName: AnalyticsEvent;
  threshold?: number; // Percentage of element that must be visible (0-1)
  trackOnce?: boolean; // Only track the first time element comes into view
}

const onIntersectionObserved = (
  eventName: AnalyticsEvent,
  entry: IntersectionObserverEntry,
) => {
  // console.log(`Element intersected for event ${eventName} with ratio ${entry.intersectionRatio}`);
  trackEvent(eventName, {
    timestamp: new Date().toISOString(),
    visibilityRatio: entry.intersectionRatio,
  });
};

/**
 * Component that tracks when its children come into viewport
 * Uses Intersection Observer API for efficient viewport detection
 */
export default function AnalyticsViewportTracker({
  children,
  eventName,
  threshold = 0.5,
  trackOnce = true,
}: AnalyticsViewportTrackerProps) {
  const elementRef = useRef<HTMLDivElement>(null);
  const [hasTracked, setHasTracked] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Element is visible in viewport
          if (entry.isIntersecting) {
            // Track only if we haven't tracked before (when trackOnce is true)
            if (!trackOnce || !hasTracked) {
              onIntersectionObserved(eventName, entry);

              // Only mark as tracked when trackOnce is true
              if (trackOnce) {
                setHasTracked(true);
              }
            }
          }
        });
      },
      {
        threshold,
        rootMargin: "0px",
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [eventName, threshold, trackOnce, hasTracked]);

  return <div ref={elementRef}>{children}</div>;
}
